import React, { useEffect, useState, useCallback, useRef, useMemo } from "react"
import * as XLSX from "xlsx"
import { useLocation, useNavigate, Link, useParams } from "react-router-dom"
import KeywordTrackingDetailsModal from "../../components/Common/Modal/KeywordTrackingDetailsModal"
import AppsTrackingPlanModal from "../../components/Common/Modal/AppsTrackingPlanModal"
import UnsavedChangesModal from "../../components/Common/Modal/UnsavedChangesModal"
import SuggestedKeywordsModal from "../../components/Common/Modal/SuggestedKeywordsModal"
import userProfileApi from "../../services/userProfileApi"
import { useKeywordTrackingAPI } from "./hooks/useKeywordTrackingAPI"
import { getCountryName, getCountryFlag } from "./utils"
import FlagSelectDropdown, { KEYWORD_TRACKING_THEME } from "../../components/Common/FlagSelectDropdown"

// Components
import AppHeader from "./components/AppHeader"
import FilterControls from "./components/FilterControls"
import KeywordSearch from "./components/KeywordSearch"
import KeywordTrackingTable from "./components/KeywordTrackingTable"
import DateRangeSelector from "./components/DateRangeSelector"
import SkeletonCard from "./components/SkeletonCard"
// This page used to run its own local react-toastify ToastContainer, which
// rendered plain unstyled top-right toasts — visually inconsistent with the
// rest of the app (including this feature's own landing page), which all
// use the shared `toastr`-based helper below: dark, pink-branded, bottom-
// right, and configured with preventDuplicates so adding several keywords
// in a row stacks cleanly instead of piling up duplicate popups.
import { triggerToast } from "../../utils/toast"
import { api } from "../../utils/variables"
import axios from "axios"
import { BUTTON_STYLES, COLORS } from "./constants"
import { Button, Modal, ModalBody } from "reactstrap"
import SEO from "../../components/Common/SEO"
import { generateSlug } from "../../utils/seoUtils"
import NotFound from "../../components/Common/NotFound"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

const COUNTRIES_DATA_KEY = "keyword_tracking_countries_data"

// Function to get stored countries data
const getStoredCountriesData = () => {
  try {
    const data = localStorage.getItem(COUNTRIES_DATA_KEY)
    return data ? JSON.parse(data) : []
  } catch (error) {
    console.warn('Error reading countries data from localStorage:', error)
    // Clear corrupted data
    try {
      localStorage.removeItem(COUNTRIES_DATA_KEY)
    } catch (clearError) {
      console.warn('Error clearing corrupted countries data:', clearError)
    }
    return []
  }
}

// Function to store countries data
const storeCountriesData = data => {
  if (!data || data.length === 0) return

  try {
    const serialized = JSON.stringify(data)
    localStorage.setItem(COUNTRIES_DATA_KEY, serialized)
  } catch (error) {
    console.warn('Error storing countries data to localStorage:', error)
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, attempting to clear old data')
      // Try to clear some old data and retry
      try {
        const keys = localStorage && typeof localStorage === 'object' ?
          Object.keys(localStorage)
            .filter(key => key.startsWith('keyword_tracking_'))
            .slice(0, 10) : [] // Remove oldest 10 entries

        keys.forEach(key => {
          try {
            localStorage.removeItem(key)
          } catch (removeError) {
            console.warn(`Error removing key ${key}:`, removeError)
          }
        })

        // Retry storing
        localStorage.setItem(COUNTRIES_DATA_KEY, JSON.stringify(data))
      } catch (retryError) {
        console.warn('Failed to store countries data after cleanup:', retryError)
      }
    }
  }
}

// Storage keys
const getStorageKey = (itemId, key) => `keyword_tracking_${itemId}_${key}`

const getStoredValue = (itemId, key, defaultValue) => {
  if (!itemId) return defaultValue

  try {
    const value = localStorage.getItem(getStorageKey(itemId, key))
    return value ? JSON.parse(value) : defaultValue
  } catch (error) {
    console.warn(`Error reading ${key} from localStorage:`, error)
    // Clear corrupted data
    try {
      localStorage.removeItem(getStorageKey(itemId, key))
    } catch (clearError) {
      console.warn(`Error clearing corrupted ${key}:`, clearError)
    }
    return defaultValue
  }
}

const storeValue = (itemId, key, value) => {
  if (!itemId) return

  try {
    const serialized = JSON.stringify(value)
    localStorage.setItem(getStorageKey(itemId, key), serialized)
  } catch (error) {
    console.warn(`Error storing ${key} to localStorage:`, error)
    if (error.name === 'QuotaExceededError') {
      console.warn('localStorage quota exceeded, attempting cleanup')
      // Try to clear some old data
      try {
        const keys = localStorage && typeof localStorage === 'object' ?
          Object.keys(localStorage)
            .filter(k => k.startsWith('keyword_tracking_') && k !== getStorageKey(itemId, key))
            .slice(0, 5) : [] // Remove oldest 5 entries

        keys.forEach(k => {
          try {
            localStorage.removeItem(k)
          } catch (removeError) {
            console.warn(`Error removing key ${k}:`, removeError)
          }
        })

        // Retry storing
        localStorage.setItem(getStorageKey(itemId, key), JSON.stringify(value))
      } catch (retryError) {
        console.warn(`Failed to store ${key} after cleanup:`, retryError)
      }
    }
  }
}

// Simple debounce function
const debounce = (func, delay) => {
  let timeoutId
  return (...args) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      func(...args)
    }, delay)
  }
}
const ExportModal = ({ show, handleClose, gameId, dataType }) => {
  const formatDateToYYYYMMDD = date => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, "0")
    const day = String(date.getDate()).padStart(2, "0")
    return `${year}-${month}-${day}`
  }

  const [fromDate, setFromDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [toDate, setToDate] = useState(formatDateToYYYYMMDD(new Date()))
  const [loading, setLoading] = useState(false)
  const { trackEvent } = useAnalytics()

  const getCsv = async () => {
    const data = {
      game: gameId, // <- pass down from props
      type: "keyword", // <- or make this dynamic
      start_date: fromDate,
      end_date: toDate,
    }

    try {
      setLoading(true)
      const res = await axios.post(`${api}/analytics/generate_csv`, data, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authToken")}`,
        },
      })

      if (res.data?.link) {
        const link = document.createElement("a")
        link.href = res.data.link
        link.setAttribute("download", "")
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)

        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_EXPORTED, {
          from_date: fromDate,
          to_date: toDate,
        })

        triggerToast("Export successful!", "success")
        handleClose()
      } else {
        triggerToast("No CSV link returned from server.", "error")
      }
    } catch (err) {
      console.error("Export failed", err)
      triggerToast("Export failed. Please try again.", "error")
    } finally {
      setLoading(false)
    }
  }

  if (!show) return null

  return (
    <div className="modal-overlay" style={modalOverlayStyle}>
      <div className="modal-container" style={modalContainerStyle}>
        <div className="modal-header" style={modalHeaderStyle}>
          <h3
            style={{
              margin: 0,
              fontSize: "18px",
              fontWeight: 600,
              color: "white",
            }}
          >
            Select Date Range
          </h3>
          <button onClick={handleClose} style={closeButtonStyle}>
            ×
          </button>
        </div>
        <div className="modal-body" style={{ padding: "24px" }}>
          <div
            className="date-inputs"
            style={{ display: "flex", gap: "20px", marginBottom: "32px" }}
          >
            <div className="date-group" style={{ flex: 1 }}>
              <label style={labelStyle}>From</label>
              <input
                type="date"
                value={fromDate}
                max={formatDateToYYYYMMDD(new Date())}
                onChange={e => setFromDate(e.target.value)}
                style={inputStyle}
              />
            </div>
            <div className="date-group" style={{ flex: 1 }}>
              <label style={labelStyle}>To</label>
              <input
                type="date"
                value={toDate}
                max={formatDateToYYYYMMDD(new Date())}
                onChange={e => setToDate(e.target.value)}
                style={inputStyle}
              />
            </div>
          </div>
          <button
            onClick={getCsv}
            disabled={loading}
            style={{
              ...exportButtonStyle,
              opacity: loading ? 0.7 : 1,
              cursor: loading ? "not-allowed" : "pointer",
            }}
          >
            {loading ? "Exporting..." : "Export"}
          </button>
        </div>
      </div>
    </div>
  )
}
const modalOverlayStyle = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: "rgba(0, 0, 0, 0.7)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  zIndex: 1000,
}
const modalContainerStyle = {
  background: "#2a2d3a",
  borderRadius: 8,
  width: 400,
  maxWidth: "90vw",
  color: "white",
}
const modalHeaderStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "20px 24px",
  borderBottom: "1px solid #3a3d4a",
}
const closeButtonStyle = {
  background: "none",
  border: "none",
  color: "#888",
  fontSize: 24,
  cursor: "pointer",
  padding: 0,
  lineHeight: 1,
}
const labelStyle = {
  display: "block",
  color: "#b0b3b8",
  fontSize: 14,
  marginBottom: 8,
  fontWeight: 500,
}
const inputStyle = {
  width: "100%",
  padding: "12px 16px",
  background: "#1e2028",
  border: "1px solid #3a3d4a",
  borderRadius: 6,
  color: "white",
  fontSize: 14,
  outline: "none",
}
const exportButtonStyle = {
  width: "100%",
  background: "linear-gradient(135deg, #F6255A 0%, #F6255A 100%)",
  color: "white",
  border: "none",
  borderRadius: 6,
  padding: "14px 24px",
  fontSize: 16,
  fontWeight: 600,
  cursor: "pointer",
}

const KeywordDetails = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { appName } = useParams()
  const { trackEvent } = useAnalytics()
  const queryParams = new URLSearchParams(location.search)
  const item_id =
    queryParams.get("item_id") ||
    queryParams.get("id") ||
    location.state?.item_id ||
    localStorage.getItem("tracked_item_id")
  const [showExportModal, setShowExportModal] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState(false)
  const [showSuggestedKeywordsModal, setShowSuggestedKeywordsModal] = useState(false)
  const [showLimitWarningModal, setShowLimitWarningModal] = useState(false)
  const [limitWarningMessage, setLimitWarningMessage] = useState('')
  const [pendingNavigation, setPendingNavigation] = useState(null)
  const [isNavigating, setIsNavigating] = useState(false)
  const [selectedCountry, setSelectedCountry] = useState(() =>
    getStoredValue(item_id, "selected_country", "")
  )
  const [availableLanguages, setAvailableLanguages] = useState([])
  const [selectedLanguage, setSelectedLanguage] = useState(() =>
    getStoredValue(item_id, "selected_language", "")
  )
  const [activeKeyword, setActiveKeyword] = useState(() =>
    getStoredValue(item_id, "active_keyword", null)
  )
  const [searchInput, setSearchInput] = useState("")
  const [pendingKeywords, setPendingKeywords] = useState(new Set())
  const [pendingPairs, setPendingPairs] = useState(new Set())
  const [isTrackingInProgress, setIsTrackingInProgress] = useState(false)
  const [trackedKeywords, setTrackedKeywords] = useState(() =>
    getStoredValue(item_id, "tracked_keywords", [])
  )
  const [trackedCountries, setTrackedCountries] = useState(() =>
    getStoredValue(item_id, "tracked_countries", [])
  )
  const [trackedCountryLanguagePairs, setTrackedCountryLanguagePairs] =
    useState(() =>
      getStoredValue(item_id, "tracked_country_language_pairs", [])
    )
  const [activeTrackedCountry, setActiveTrackedCountry] = useState(() =>
    getStoredValue(item_id, "active_tracked_country", "")
  )
  const [activeTrackedPair, setActiveTrackedPair] = useState(() =>
    getStoredValue(item_id, "active_tracked_pair", "")
  )
  const [selectionError, setSelectionError] = useState(false)
  const [fromDate, setFromDate] = useState(new Date("2024-08-08"))
  const [toDate, setToDate] = useState(new Date())
  const [showAllKeywords, setShowAllKeywords] = useState(false)
  const [windowWidth, setWindowWidth] = useState(() => window.innerWidth)
  const [initialFetchDone, setInitialFetchDone] = useState(false) // Start with no fetch done
  const [hasCompletedInitialFetch, setHasCompletedInitialFetch] = useState(false) // Prevent multiple initial fetches
  // Memoize modal results to avoid re-renders

  // Add a state to track keywords specific to each country-language pair
  const [keywordsByCountryLanguage, setKeywordsByCountryLanguage] = useState(
    () => getStoredValue(item_id, "keywords_by_country_language", {})
  )
  // On a fresh browser/session there is no localStorage cache, so the state
  // above starts as `{}` — indistinguishable from "account genuinely tracks
  // 0 keywords" until the mount-time fetch below resolves. Anything that reads
  // keywordsByCountryLanguage to show a count (e.g. the AI Keyword
  // Recommendations modal's "slots remaining", or the tracked-pair count
  // badges) should wait for this flag rather than trusting an empty object.
  const [keywordTrackingDataLoaded, setKeywordTrackingDataLoaded] = useState(false)

  // Keep windowWidth in sync with actual window size
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Fetch current plan data
  useEffect(() => {
    const fetchPlanData = async () => {
      try {
        setPlanLoading(true)

        // Fetch both current plan and all products
        const [currentPlanResponse, productsResponse] = await Promise.all([
          userProfileApi.fetchCurrentPlan(),
          userProfileApi.fetchPlans()
        ])

        // Find the current plan details from products
        if (currentPlanResponse?.data?.product && productsResponse?.data) {
          const currentProduct = productsResponse.data.find(
            product => product.id === currentPlanResponse.data.product
          )

          if (currentProduct) {
            const planData = {
              ...currentPlanResponse.data,
              productDetails: currentProduct
            }
            setCurrentPlan(planData)
          }
        }
      } catch (error) {
        console.error('Error fetching plan data:', error)
      } finally {
        setPlanLoading(false)
      }
    }

    fetchPlanData()
  }, [])

  // Helper function to extract keyword limit from plan data
  const getKeywordLimit = () => {
    // If no active subscription, default to Explorer plan (5 keywords)
    if (!currentPlan || !currentPlan.productDetails) {
      return '5'
    }

    const benefits = currentPlan.productDetails.description || []

    // Try multiple variations of benefit names - prioritize exact match first
    const keywordBenefit = benefits.find(benefit => {
      if (!benefit.benefit_name) return false
      const benefitName = benefit.benefit_name.toLowerCase()

      // Exclude benefits about searches/exploration
      if (benefitName.includes('explorer') || benefitName.includes('searches')) return false

      // First try exact match for "Keyword & Category Rank"
      if (benefitName === 'keyword & category rank') return true

      // Then try partial matches for rank-related benefits
      return benefitName.includes('keyword & category rank') ||
        (benefitName.includes('keyword') && benefitName.includes('rank'))
    })

    if (keywordBenefit) {
      const value = keywordBenefit.benefit_value

      // Try multiple regex patterns to handle different formats
      const patterns = [
        /upto\s*(\d+)\s*keyword\s*(?:\/categories)?/i,
        /up\s*to\s*(\d+)\s*keyword/i,
        /(\d+)\s*keyword/i,
        /(\d+)\s*keywords/i,
        /unlimited/i,
        /(\d+)\s*apps?:?\s*upto\s*(\d+)\s*keyword/i,  // "20 Apps: upto 100 keyword"
        /(\d+)\s*apps?:?\s*up\s*to\s*(\d+)\s*keyword/i,
        /(\d+)-app\s*upto\s*(\d+)\s*keyword/i,  // "1-App upto 5 keyword"
        /(\d+)\s*app\s*->\s*upto\s*(\d+)\s*keyword/i,  // "5 App -> upto 15 keyword"
        /keyword\s*limit:\s*(\d+)/i,
        /(\d+)\s*keyword\s*limit/i,
        /(\d+)\s*keyword\s*per\s*app/i
      ]

      for (const pattern of patterns) {
        const match = value.match(pattern)
        if (match) {
          // For patterns with multiple groups, return the last one (keyword limit)
          // For patterns with single group, return the first one
          const result = match[match.length - 1]
          return result || 'Unlimited'
        }
      }
    }

    // If no keyword benefit found, default to Explorer plan (5 keywords)
    return '5'
  }

  // Helper function to get real-time keyword count from input
  const getRealTimeKeywordCount = () => {
    if (!searchInput || searchInput.trim() === '') return 0

    // Split by comma and filter out empty strings and whitespace
    const keywords = searchInput
      .split(',')
      .map(keyword => keyword.trim())
      .filter(keyword => keyword.length > 0)

    return keywords.length
  }

  // Calculate total keywords (tracked only, excluding input until added)
  const getTotalKeywordCount = () => {
    return Object.values(keywordsByCountryLanguage).reduce((total, keywords) => {
      return total + (keywords || []).filter(k => k !== 'game_data').length
    }, 0)
  }

  // Calculate total keywords including input (for limit warnings)
  const getTotalKeywordCountWithInput = () => {
    return trackedKeywords.length + getRealTimeKeywordCount()
  }

  // Check if adding keywords would exceed limit
  const wouldExceedLimit = () => {
    const limit = getKeywordLimit()
    if (limit === 'Unlimited') return false

    const totalAfterAdding = getTotalKeywordCountWithInput()
    return totalAfterAdding > parseInt(limit)
  }

  // Check if limit is already reached (based on tracked keywords only, not input)
  const isLimitReached = () => {
    const limit = getKeywordLimit()
    if (limit === 'Unlimited') return false

    return getTotalKeywordCount() >= parseInt(limit)
  }

  // Get number of keywords to remove to stay within limit
  const getKeywordsToRemove = () => {
    const limit = getKeywordLimit()
    if (limit === 'Unlimited') return 0

    const totalAfterAdding = getTotalKeywordCountWithInput()
    const excess = totalAfterAdding - parseInt(limit)
    return excess > 0 ? excess : 0
  }

  // Initialize countriesData from localStorage if available
  const [countriesDataLoaded, setCountriesDataLoaded] = useState(false)
  const beforeUnloadRef = useRef(null)

  // Plan data state
  const [currentPlan, setCurrentPlan] = useState(null)
  const [planLoading, setPlanLoading] = useState(true)

  // Helper function to get the current pair key
  const getCurrentPairKey = () => {
    if (selectedCountry && selectedLanguage) {
      return `${selectedCountry}_${selectedLanguage}`
    }
    return null
  }

  // Pair key derived from the active tab — used for the chip display so that
  // changing the country dropdown does not wipe the visible tracked keywords.
  const getActivePairKey = () => {
    if (activeTrackedPair) {
      // "Australia (english)" → "Australia_english"
      return activeTrackedPair.replace(' (', '_').replace(')', '')
    }
    return getCurrentPairKey()
  }

  // Helper function to get keywords for the current country-language pair
  const getKeywordsForCurrentPair = () => {
    const pairKey = getCurrentPairKey()

    if (pairKey && keywordsByCountryLanguage[pairKey]) {
      const keywords = keywordsByCountryLanguage[pairKey].filter(
        keyword => keyword !== "game_data"
      )
      return keywords
    }
    return []
  }

  // Keywords for the chip display — follows activeTrackedPair tab, not the dropdown
  const getKeywordsForActivePair = () => {
    const pairKey = getActivePairKey()
    if (pairKey && keywordsByCountryLanguage[pairKey]) {
      return keywordsByCountryLanguage[pairKey].filter(k => k !== "game_data")
    }
    return []
  }


  // Helper function to get allowed keywords for current plan
  const getAllowedKeywords = () => {
    const limit = getKeywordLimit()
    if (limit === 'Unlimited') return Infinity
    return parseInt(limit)
  }

  // Helper function to check if user is over limit
  const isOverKeywordLimit = () => {
    const totalKeywords = getTotalKeywordCount()
    const allowedLimit = getAllowedKeywords()
    return totalKeywords > allowedLimit
  }


  // Upgrade modal state
  const [showUpgradeModal, setShowUpgradeModal] = useState(false)
  const [keywordToDelete, setKeywordToDelete] = useState(null)
  const [showDeleteKeywordModal, setShowDeleteKeywordModal] = useState(false)
  const [pairToRemove, setPairToRemove] = useState(null)
  const isUpgradeError = error => {
    const response = error?.response
    const data = response?.data || {}
    const message = data?.message || ""
    const action = data?.action || ""
    return (
      response?.status === 400 &&
      (action === "UPGRADE" || /max limit/i.test(message))
    )
  }

  const isUpgradeResponse = response => {
    if (!response || typeof response !== "object") return false

    // If using axios, real payload may be in response.data
    const data = response?.data || response

    const status = data?.status
    const action = data?.action
    const message = data?.message || ""

    return (
      status === 400 && (action === "UPGRADE" || /max limit/i.test(message))
    )
  }

  // Helper function to check if app has unsaved changes
  // Since all changes are persisted to the backend immediately, there's no
  // truly "unsaved" state — return false to avoid blocking navigation.
  const hasUnsavedChanges = useCallback(() => {
    return false
  }, [])

  // Navigation blocking handler
  const handleNavigationAttempt = useCallback((targetPath) => {
    if (hasUnsavedChanges()) {
      setPendingNavigation(targetPath)
      setShowUnsavedChangesModal(true)
      return true // Block navigation
    }
    return false // Allow navigation
  }, [hasUnsavedChanges])

  // Confirm leaving without saving
  const confirmLeaveWithoutSaving = useCallback(() => {
    console.log('confirmLeaveWithoutSaving called, pendingNavigation:', pendingNavigation)
    setIsNavigating(true)
    setShowUnsavedChangesModal(false)

    // Remove beforeunload listener to prevent double confirmation
    if (beforeUnloadRef.current) {
      window.removeEventListener('beforeunload', beforeUnloadRef.current)
      beforeUnloadRef.current = null
    }

    // Small delay to show loading state
    setTimeout(() => {
      if (pendingNavigation) {
        // Use window.location.href for more reliable navigation
        window.location.href = pendingNavigation
      } else {
        // If no pending navigation, go to dashboard as default
        navigate('/dashboard')
      }
    }, 500)
  }, [pendingNavigation, navigate])

  // Cancel leaving
  const cancelLeaveWithoutSaving = useCallback(() => {
    setShowUnsavedChangesModal(false)
    setPendingNavigation(null)
    setIsNavigating(false)
  }, [])

  // Removed: useEffect that was overwriting localStorage on every render
  // This was causing removed country pairs to reappear
  // The localStorage updates are now handled properly in the specific functions where changes occur

  // Add a ref to track API calls and prevent duplicates
  const apiCallTracker = useRef({})

  // Add a ref to track keyword tracking API calls
  const keywordTrackingCallTracker = useRef({})

  // Add a ref to track the last selected pair to prevent redundant API calls when switching tabs
  const lastSelectedPairRef = useRef(null)

  // Cleanup function for API call trackers
  const cleanupApiCallTrackers = useCallback(() => {
    // Clean up old entries to prevent memory leaks
    const now = Date.now()
    const maxAge = 5 * 60 * 1000 // 5 minutes

    Object.keys(apiCallTracker.current).forEach(key => {
      if (typeof apiCallTracker.current[key] === 'object' &&
        apiCallTracker.current[key].timestamp) {
        if (now - apiCallTracker.current[key].timestamp > maxAge) {
          delete apiCallTracker.current[key]
        }
      }
    })

    Object.keys(keywordTrackingCallTracker.current).forEach(key => {
      if (typeof keywordTrackingCallTracker.current[key] === 'object' &&
        keywordTrackingCallTracker.current[key].timestamp) {
        if (now - keywordTrackingCallTracker.current[key].timestamp > maxAge) {
          delete keywordTrackingCallTracker.current[key]
        }
      }
    })
  }, [])

  // Cleanup trackers periodically
  useEffect(() => {
    const interval = setInterval(cleanupApiCallTrackers, 60000) // Every minute
    return () => clearInterval(interval)
  }, [cleanupApiCallTrackers])

  const {
    countriesData,
    predefinedKeywords,
    keywordsLoading,
    trackedApp,
    trackedKeywordSummary,
    keywordTrackingData,
    addingKeyword,
    fetchKeywordTracking,
    addKeywordTracking,
    removeEntity,
    notFoundError,
  } = useKeywordTrackingAPI(item_id, getStoredCountriesData())

  // URL Validation and Correction
  useEffect(() => {
    // Only proceed if we have valid app data and haven't already redirected
    if (trackedApp && trackedApp.name && item_id) {
      const expectedSlug = generateSlug(trackedApp.name);

      // Validate that we have a proper slug (not empty or just special characters)
      if (!expectedSlug || expectedSlug.trim() === '') {
        console.warn('Invalid app name for slug generation:', trackedApp.name);
        return;
      }

      // Only redirect if there's a genuine mismatch and we're not already on the correct URL
      const needsRedirect = (!appName || appName !== expectedSlug) &&
        window.location.pathname !== `/xr/keyword/${expectedSlug}`;

      if (needsRedirect) {
        // Add a flag to prevent multiple redirects
        const redirectKey = `keyword_redirect_${item_id}`;
        const lastRedirect = sessionStorage.getItem(redirectKey);
        const now = Date.now();

        // Prevent multiple redirects within 5 seconds
        if (!lastRedirect || (now - parseInt(lastRedirect)) > 5000) {
          sessionStorage.setItem(redirectKey, now.toString());

          navigate(`/xr/keyword/${expectedSlug}?id=${item_id}`, {
            replace: true,
            state: location.state // Preserve state
          });
        }
      }
    }
  }, [trackedApp?.name, appName, navigate, location.state, item_id]);

  // Add debugging for date range
  useEffect(() => {
    console.log("Date range changed:", { fromDate, toDate })
  }, [fromDate, toDate])

  // Add debugging for predefined keywords
  useEffect(() => {
    console.log("Predefined keywords loaded:", predefinedKeywords.length)
  }, [predefinedKeywords])

  // Add beforeunload event listener for browser back/refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasUnsavedChanges()) {
        e.preventDefault()
        e.returnValue = 'You have unsaved changes. Are you sure you want to leave?'
        return e.returnValue
      }
    }

    // Store reference for cleanup
    beforeUnloadRef.current = handleBeforeUnload

    // Intercept link clicks - simplified version
    const handleLinkClick = (e) => {
      try {
        const target = e.target.closest('a')
        if (target && hasUnsavedChanges()) {
          const href = target.getAttribute('href')
          if (href && (href.startsWith('/') || href.includes(window.location.hostname))) {
            e.preventDefault()
            e.stopPropagation()
            setPendingNavigation(href)
            setShowUnsavedChangesModal(true)
            return false
          }
        }
      } catch (error) {
        console.warn('Error in handleLinkClick:', error)
      }
      return true
    }

    // Listen for browser navigation
    const handlePopState = (e) => {
      try {
        if (hasUnsavedChanges()) {
          e.preventDefault()
          e.stopPropagation()
          window.history.pushState(null, null, window.location.pathname)
          setShowUnsavedChangesModal(true)
          return false
        }
      } catch (error) {
        console.warn('Error in handlePopState:', error)
      }
    }

    // Add event listeners with error handling
    try {
      window.addEventListener('beforeunload', handleBeforeUnload, { passive: false })
      document.addEventListener('click', handleLinkClick, { capture: true, passive: false })
      window.addEventListener('popstate', handlePopState, { passive: false })
    } catch (error) {
      console.warn('Error adding event listeners:', error)
    }

    // Cleanup function
    return () => {
      try {
        if (beforeUnloadRef.current) {
          window.removeEventListener('beforeunload', beforeUnloadRef.current)
        }
        document.removeEventListener('click', handleLinkClick, true)
        window.removeEventListener('popstate', handlePopState)
        beforeUnloadRef.current = null
      } catch (error) {
        console.warn('Error removing event listeners:', error)
      }
    }
  }, [hasUnsavedChanges])

  // Trigger data refresh when date range changes
  useEffect(() => {
    if (selectedCountry && selectedLanguage && initialFetchDone) {
      // Reset API call tracker for current country/language
      const callKey = `${selectedCountry}_${selectedLanguage}`
      apiCallTracker.current[callKey] = {
        inProgress: false,
        timestamp: Date.now()
      }

      // Trigger data fetch with new date range
      fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
        .then(data => {
          handleKeywordTrackingResponse(data, selectedCountry, selectedLanguage)
        })
        .catch(error => {
          console.error("Error fetching data with new date range:", error)
        })
    }
  }, [fromDate, toDate, selectedCountry, selectedLanguage, initialFetchDone])


  useEffect(() => {
    if (location.state?.item_id) {
      localStorage.setItem("tracked_item_id", location.state.item_id)
    }
  }, [location.state])

  // Modify the useEffect that handles the initial API response
  useEffect(() => {
    // When countriesData is loaded from the API, store it in localStorage
    if (countriesData.length > 0 && !countriesDataLoaded) {
      storeCountriesData(countriesData)
      setCountriesDataLoaded(true)
    }
  }, [countriesData, countriesDataLoaded])

  // Only restore selections from localStorage - no automatic defaults
  useEffect(() => {
    // This effect now only ensures countries data is loaded but does NOT auto-select
    // Countries and languages should only be selected when user explicitly chooses them
    // or when restoring from localStorage (which is handled in useState initializers)
  }, [countriesData])

  // Set default country and language when countries data loads
  useEffect(() => {
    if (countriesData.length > 0) {
      const storedCountry = getStoredValue(item_id, "selected_country", "")
      const storedLanguage = getStoredValue(item_id, "selected_language", "")

      // If no stored country, set default to first country
      if (!storedCountry) {
        const firstCountry = countriesData[0]
        setSelectedCountry(firstCountry.name)
        storeValue(item_id, "selected_country", firstCountry.name)

        // Set default language for the first country
        if (firstCountry.locales && firstCountry.locales.length > 0) {
          const defaultLanguage = firstCountry.locales[0]
          setSelectedLanguage(defaultLanguage)
          storeValue(item_id, "selected_language", defaultLanguage)
        }
      } else if (!storedLanguage) {
        // If country exists but no language, set default language for that country
        const country = countriesData.find(c => c.name === storedCountry)
        if (country && country.locales && country.locales.length > 0) {
          const defaultLanguage = country.locales[0]
          setSelectedLanguage(defaultLanguage)
          storeValue(item_id, "selected_language", defaultLanguage)
        }
      }
    }
  }, [countriesData, item_id])

  // Load available languages for selected country when countries data is loaded
  useEffect(() => {
    if (countriesData.length > 0 && selectedCountry) {
      const found = countriesData.find(c => c.name === selectedCountry)
      if (found) {
        setAvailableLanguages(found.locales || [])
      }
    }
  }, [countriesData, selectedCountry])

  // Set initialFetchDone to true when both country and language are selected initially
  useEffect(() => {
    if (selectedCountry && selectedLanguage && !initialFetchDone) {
      // If we have selections, set initialFetchDone to true to trigger data fetch
      setInitialFetchDone(true)
    }
  }, [selectedCountry, selectedLanguage, initialFetchDone])

  // Set initial active tracked country
  useEffect(() => {
    if (trackedCountryLanguagePairs.length > 0 && !activeTrackedCountry) {
      setActiveTrackedCountry(trackedCountryLanguagePairs[0].country)
      setActiveTrackedPair(trackedCountryLanguagePairs[0].pair)
    }
  }, [trackedCountryLanguagePairs, activeTrackedCountry])

  // Set up global import click handler for mobile layout
  useEffect(() => {
    window.onImportClick = () => {
      document.getElementById('keywordFileUpload').click()
    }

    return () => {
      // Clean up global handler
      delete window.onImportClick
    }
  }, [])

  // Store values in refs to avoid dependency changes triggering re-renders
  const trackedKeywordsRef = useRef(trackedKeywords)
  const activeKeywordRef = useRef(activeKeyword)
  const itemIdRef = useRef(item_id)
  const fetchKeywordTrackingRef = useRef(fetchKeywordTracking)
  const addKeywordTrackingRef = useRef(addKeywordTracking)
  const debouncedFetchRef = useRef(null)

  // Create a function to handle the API response and update state
  const handleKeywordTrackingResponse = useCallback(
    (data, country, language) => {
      if (!data) return


      // Convert country code to full name for consistency
      const countryName = getCountryName(country, countriesData)

      // Extract keywords from data and update keywordsByCountryLanguage
      if (data && typeof data === "object") {
        const pairKey = `${countryName}_${language}`
        const keywords = Object.keys(data).filter(key => key !== "game_data")

        // Only add the pair if we actually have keywords
        if (keywords.length > 0) {
          const pair = `${countryName} (${language})`

          setTrackedCountryLanguagePairs(prev => {
            if (prev.some(p => p === pair)) return prev
            return [...prev, pair]
          })

          setActiveTrackedPair(pair)

          setTrackedCountries(prev => {
            if (prev.includes(countryName)) return prev
            return [...prev, countryName]
          })
        }

        // Merge API data with local state — never replace. Replacing causes the badge
        // count to drop when the backend hasn't indexed newly added keywords yet.
        // Also persist to localStorage so badges survive page reloads for all pairs.
        if (keywords.length > 0) {
          setKeywordsByCountryLanguage(prev => {
            const updated = {
              ...prev,
              [pairKey]: [...new Set([...(prev[pairKey] || []), ...keywords])],
            }
            storeValue(item_id, "keywords_by_country_language", updated)
            return updated
          })
        }

        // REMOVED: The automatic adding of active keyword when changing country/language
        // This was causing excessive API calls
      }
    },
    [countriesData, item_id] // re-create when countriesData or item_id changes
  )

  // Update refs when values change
  useEffect(() => {
    trackedKeywordsRef.current = trackedKeywords
    activeKeywordRef.current = activeKeyword
    itemIdRef.current = item_id
    fetchKeywordTrackingRef.current = fetchKeywordTracking
    addKeywordTrackingRef.current = addKeywordTracking

    // Create a debounced version of fetchKeywordTracking that also handles the response
    debouncedFetchRef.current = debounce((country, language) => {
      fetchKeywordTracking(country, language, fromDate, toDate)
        .then(data => {
          handleKeywordTrackingResponse(data, country, language)
        })
        .catch(error => {
          console.error("Error fetching tracking data:", error)
          // Reset the tracker on error so we can try again
          const callKey = `${country}_${language}`
          apiCallTracker.current[callKey] = {
            inProgress: false,
            timestamp: Date.now()
          }
        })
    }, 300) // 300ms debounce time
  }, [
    trackedKeywords,
    activeKeyword,
    item_id,
    fetchKeywordTracking,
    addKeywordTracking,
    handleKeywordTrackingResponse,
    fromDate,
    toDate,
  ])

  // Initial data fetch on component mount
  useEffect(() => {
    // Only run once when component mounts and we have an item_id
    // AND we haven't completed initial fetch yet
    if (item_id && !hasCompletedInitialFetch) {
      // Make initial API call without country/language
      fetchKeywordTracking(null, null, fromDate, toDate)
        .then(response => {
          // The response format is expected to be:
          // { status: 200, message: "...", action: "DISPLAY", data: { "keyword1": {...}, "keyword2": {...} } }
          if (response && response.status === 200 && response.data) {
            const data = response.data

            // Extract keywords from the response, filtering out game_data
            const keywords = Object.keys(data).filter(
              key => key !== "game_data"
            )
            if (keywords.length > 0) {
              // Use API data as source of truth, don't merge with localStorage
              // This prevents re-adding removed keywords on refresh
              setTrackedKeywords(keywords)
              storeValue(item_id, "tracked_keywords", keywords)

              // If no active keyword is set, use the first one from the API
              if (!activeKeyword && keywords.length > 0) {
                setActiveKeyword(keywords[0])
                storeValue(item_id, "active_keyword", keywords[0])
              }

              // Extract countries and languages from the response
              const countryLanguagePairs = new Set()
              const countries = new Set()
              console.log("keywordData", data)
              Object.values(data).forEach(keywordData => {
                if (keywordData.country && keywordData.language) {
                  // Convert country code to full name for display
                  const countryName = getCountryName(keywordData.country, countriesData)
                  const pair = `${countryName} (${keywordData.language})`
                  countryLanguagePairs.add(pair)

                  // Also track the country separately (convert to full name)
                  countries.add(countryName)
                }
              })

              // Update tracked country-language pairs — merge API data with existing
              // localStorage so pairs tracked in previous sessions aren't wiped
              // when the API response doesn't include every pair (e.g., empty pairs)
              if (countryLanguagePairs.size > 0) {
                const apiPairsList = Array.from(countryLanguagePairs)
                setTrackedCountryLanguagePairs(prev => {
                  const merged = [...new Set([...prev, ...apiPairsList])]
                  console.log("LOGGINGGGG HEREEEE 1152", apiPairsList, countryLanguagePairs);

                  storeValue(item_id, "tracked_country_language_pairs", merged)
                  return merged
                })
              }

              // Update tracked countries — same merge approach
              if (countries.size > 0) {
                const apiCountriesList = Array.from(countries)
                setTrackedCountries(prev => {
                  const merged = [...new Set([...prev, ...apiCountriesList])]
                  storeValue(item_id, "tracked_countries", merged)
                  return merged
                })
              }

              // Build keywordsByCountryLanguage by mapping each keyword to its own pair.
              // Previously this assigned ALL keywords to every pair, which caused
              // stillNeededElsewhere checks to always return true and broke per-pair removal.
              setKeywordsByCountryLanguage(prev => {
                const updated = { ...prev }

                Object.entries(data).forEach(([keyword, keywordData]) => {
                  if (keyword === "game_data") return
                  if (keywordData && keywordData.country && keywordData.language) {
                    const countryName = getCountryName(keywordData.country, countriesData)
                    const pairKey = `${countryName}_${keywordData.language}`
                    if (!updated[pairKey]) updated[pairKey] = []
                    if (!updated[pairKey].includes(keyword)) updated[pairKey].push(keyword)
                  }
                })

                storeValue(item_id, "keywords_by_country_language", updated)
                return updated
              })
            }

            // Set flag to prevent future initial fetches
            setHasCompletedInitialFetch(true)
          }
        })
        .catch(error => {
          console.error("Error in initial API call on mount:", error)
          // Still set flag to prevent repeated failed attempts
          setHasCompletedInitialFetch(true)
        })
        .finally(() => {
          setKeywordTrackingDataLoaded(true)
        })
    } else if (!item_id) {
      // No item_id to fetch for — nothing to wait on.
      setKeywordTrackingDataLoaded(true)
    }
    // Only depend on item_id and fetchKeywordTracking to prevent multiple calls
    // But we also want to re-evaluate when countriesData loads to ensure proper naming
  }, [item_id, fetchKeywordTracking, countriesData.length, hasCompletedInitialFetch])

  // Seed Tracked Data Availability from the full per-game locale list, so
  // locales without keyword data yet (from get_keyword_tracking) still show up
  useEffect(() => {
    const localDetails = trackedKeywordSummary?.local_details
    if (!localDetails || localDetails.length === 0 || countriesData.length === 0) return

    const countryLanguagePairs = new Set()
    const countries = new Set()
    localDetails.forEach(detail => {
      if (detail.country && detail.language) {
        const countryName = getCountryName(detail.country, countriesData)
        countryLanguagePairs.add(`${countryName} (${detail.language})`)
        countries.add(countryName)
      }
    })

    if (countryLanguagePairs.size > 0) {
      const summaryPairsList = Array.from(countryLanguagePairs)
      setTrackedCountryLanguagePairs(prev => {
        const merged = [...new Set([...prev, ...summaryPairsList])]
        storeValue(item_id, "tracked_country_language_pairs", merged)
        return merged
      })
    }

    if (countries.size > 0) {
      const summaryCountriesList = Array.from(countries)
      setTrackedCountries(prev => {
        const merged = [...new Set([...prev, ...summaryCountriesList])]
        storeValue(item_id, "tracked_countries", merged)
        return merged
      })
    }
  }, [trackedKeywordSummary, countriesData, item_id])

  // � Auto-select the first tracked pair if none is active
  useEffect(() => {
    // Only run if we have tracked pairs but no active selection
    if (trackedCountryLanguagePairs.length > 0 && !activeTrackedPair && !selectedCountry) {
      const firstPair = trackedCountryLanguagePairs[0]

      // Basic validation format check
      const match = firstPair.match(/(.*) \((.*)\)/)

      if (match) {
        // Log for debugging
        console.log("Auto-selecting first tracked pair:", firstPair)

        setActiveTrackedPair(firstPair)
        storeValue(item_id, "active_tracked_pair", firstPair)

        const country = match[1]
        const language = match[2]

        setSelectedCountry(country)
        setSelectedLanguage(language)
        storeValue(item_id, "selected_country", country)
        storeValue(item_id, "selected_language", language)

        // Also set active tracked country
        setActiveTrackedCountry(country)
        storeValue(item_id, "active_tracked_country", country)

        // IMPORTANT: Trigger the data fetch
        // We defer this slightly to ensure state updates propagate
        setTimeout(() => {
          setInitialFetchDone(true)
        }, 0)
      }
    }
  }, [trackedCountryLanguagePairs, activeTrackedPair, selectedCountry, item_id])

  // �🧠 Fetch keyword tracking when country and language are selected - with duplicate prevention
  useEffect(() => {
    // Skip if we don't have necessary data
    if (!selectedCountry || !selectedLanguage || !initialFetchDone) {
      return
    }

    // Create a unique key for this country/language combination
    const callKey = `${selectedCountry}_${selectedLanguage}`
    const currentPair = `${selectedCountry}_${selectedLanguage}`

    // Skip if this is the same as the last selected pair (prevents redundant calls when clicking tabs)
    if (lastSelectedPairRef.current === currentPair) {
      return
    }

    // Update the last selected pair
    lastSelectedPairRef.current = currentPair

    // Check if we've already made this exact API call recently
    const callInfo = apiCallTracker.current[callKey]
    if (callInfo && (Date.now() - callInfo.timestamp) < 3000) { // 3 second debounce
      return
    }

    // Mark that we're making this API call with timestamp
    apiCallTracker.current[callKey] = {
      inProgress: true,
      timestamp: Date.now()
    }


    // Use direct fetchKeywordTracking to ensure immediate response
    fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
      .then(data => {
        // Process the response data
        handleKeywordTrackingResponse(data, selectedCountry, selectedLanguage)
      })
      .catch(error => {
        console.error("Error fetching tracking data:", error)
        // Reset the tracker on error so we can try again
        apiCallTracker.current[callKey] = {
          inProgress: false,
          timestamp: Date.now()
        }
      })

    // IMPORTANT: Removed the automatic adding of keywords when changing country/language
    // This was causing excessive API calls
  }, [
    selectedCountry,
    selectedLanguage,
    initialFetchDone,
    keywordsByCountryLanguage,
    fetchKeywordTracking,
    handleKeywordTrackingResponse,
  ])

  // Restore active keyword when keywords are available
  useEffect(() => {
    const currentPairKey = getCurrentPairKey()
    if (
      currentPairKey &&
      keywordsByCountryLanguage[currentPairKey] &&
      keywordsByCountryLanguage[currentPairKey].length > 0
    ) {
      // If we have keywords for the current pair but no active keyword, set the first one as active
      if (!activeKeyword) {
        const firstKeyword = keywordsByCountryLanguage[currentPairKey].find(
          k => k !== "game_data"
        )
        if (firstKeyword) {
          setActiveKeyword(firstKeyword)
          storeValue(item_id, "active_keyword", firstKeyword)
        }
      }
    }
  }, [
    keywordsByCountryLanguage,
    selectedCountry,
    selectedLanguage,
    activeKeyword,
    item_id,
  ])

  // Additional effect to ensure predefined keywords stay selected
  useEffect(() => {
    // If we have an active keyword from localStorage and it's a predefined keyword, keep it selected
    if (activeKeyword && predefinedKeywords.includes(activeKeyword)) {
      storeValue(item_id, "active_keyword", activeKeyword)
    }
  }, [activeKeyword, predefinedKeywords, item_id])

  // Update the handleCountryChange function to be more efficient
  // Accepts either a DOM event (from <select>) or a plain string (from custom dropdown)
  const handleCountryChange = e => {
    const selected = typeof e === 'string' ? e : e.target.value
    setSelectedCountry(selected)
    setSelectionError(false)

    let resolvedLanguage = ""

    // Update available languages for the selected country
    if (selected) {
      const found = countriesData.find(c => c.name === selected)
      if (found) {
        const languages = found.locales || []
        setAvailableLanguages(languages)

        // Select the first language if available
        if (languages.length >= 0) {
          const firstLanguage = languages[0]
          resolvedLanguage = firstLanguage || ""
          setSelectedLanguage(firstLanguage)

          // Reset API call tracker for this new country/language pair
          const callKey = `${selected}_${firstLanguage}`
          apiCallTracker.current[callKey] = {
            inProgress: false,
            timestamp: Date.now()
          }

          // Set initialFetchDone to trigger a fresh API call
          setInitialFetchDone(true)

        } else {
          setSelectedLanguage("")
        }
      }

      // Set this as the active tracked country
      setActiveTrackedCountry(selected)

      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_LOCALE_CHANGED, {
        country: selected,
        language: resolvedLanguage,
      })
    } else {
      setAvailableLanguages([])
      setSelectedLanguage("")
    }

    setActiveKeyword(null)

    // Reset all API call trackers for this country
    Object.keys(apiCallTracker.current).forEach(key => {
      if (key.startsWith(selected)) {
        apiCallTracker.current[key] = {
          inProgress: false,
          timestamp: Date.now()
        }
      }
    })

    // Reset tracked keyword trackers for this country to prevent auto-adding
    Object.keys(keywordTrackingCallTracker.current).forEach(key => {
      if (key.includes(`_${selected}_`)) {
        keywordTrackingCallTracker.current[key] = {
          inProgress: false,
          timestamp: Date.now()
        }
      }
    })
  }

  // Lightweight country select handler: populate available languages without
  // changing selectedLanguage or triggering fetch. This allows the UI to
  // show language options immediately while deferring actual selection/add
  // until the user clicks "Add" in the UI.
  const handleCountrySelect = country => {
    if (!country) {
      setAvailableLanguages([])
      return
    }
    const found = countriesData.find(c => c.name === country)
    if (found) {
      setAvailableLanguages(found.locales || [])
    } else {
      setAvailableLanguages([])
    }
  }

  // Update the handleLanguageChange function to be more efficient
  // Accepts either a DOM event (legacy <select> callers) or a plain string
  // (FlagSelectDropdown, which reports the chosen value directly).
  const handleLanguageChange = e => {
    const selectedLang = typeof e === 'string' ? e : e.target.value
    setSelectedLanguage(selectedLang)
    setSelectionError(false)

    setActiveKeyword(null)

    // Reset API call tracker for the current country and new language
    if (selectedCountry) {
      const callKey = `${selectedCountry}_${selectedLang}`
      apiCallTracker.current[callKey] = {
        inProgress: false,
        timestamp: Date.now()
      }

      // Set initialFetchDone to trigger a fresh API call
      setInitialFetchDone(true)

      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_LOCALE_CHANGED, {
        country: selectedCountry,
        language: selectedLang,
      })
    }
  }

  // Update the handleTrackedCountryClick function to prevent multiple API calls
  const handleTrackedCountryClick = useCallback(
    country => {
      // Skip if this is already the active country
      if (activeTrackedCountry === country) {
        return
      }

      // Update the UI immediately
      setActiveTrackedCountry(country)

      // Update country and related state with a slight delay to prevent multiple API calls
      setTimeout(() => {
        setSelectedCountry(country)

        // Update available languages for this country
        const found = countriesData.find(c => c.name === country)
        if (found) {
          const languages = found.locales || []
          setAvailableLanguages(languages)

          // Select the first language if available
          if (languages.length > 0) {
            const firstLanguage = languages[0]
            setSelectedLanguage(firstLanguage)

            // Reset API call tracker for this country/language
            const callKey = `${country}_${firstLanguage}`
            apiCallTracker.current[callKey] = {
              inProgress: false,
              timestamp: Date.now()
            }

            setInitialFetchDone(true) // Trigger data fetch

          }
        }
      }, 50) // Small delay to batch state updates
    },
    [countriesData, activeTrackedCountry]
  )

  // Update the handleTrackedPairClick function to prevent multiple API calls
  const handleTrackedPairClick = useCallback(
    pair => {
      // Extract country and language from the pair
      // Format is "Country (Language)"
      const match = pair.match(/(.*) \((.*)\)/)
      if (match) {
        const country = match[1]
        const language = match[2]

        // Skip if this is already the active pair
        if (activeTrackedPair === pair) {
          return
        }

        // Update the UI immediately
        setActiveTrackedPair(pair)
        setActiveTrackedCountry(country)
        setShowAllKeywords(false)

        // Update country and language with a slight delay to prevent multiple API calls
        // when rapidly clicking tabs
        setTimeout(() => {
          setSelectedCountry(country)
          setSelectedLanguage(language)

          // Update available languages for this country
          const found = countriesData.find(c => c.name === country)
          if (found) {
            setAvailableLanguages(found.locales || [])
          }

          // Reset API call tracker for this country/language
          const callKey = `${country}_${language}`
          apiCallTracker.current[callKey] = {
            inProgress: false,
            timestamp: Date.now()
          }

          setInitialFetchDone(true) // Trigger data fetch

        }, 50) // Small delay to batch state updates
      }
    },
    [activeTrackedPair, selectedCountry, selectedLanguage, countriesData]
  )

  // Update the handleKeywordClick function to be more efficient
  const handleKeywordClick = async keyword => {
    if (keyword === "game_data") return
    if (!selectedCountry || !selectedLanguage) {
      setSelectionError(true)
      return
    }
    setSelectionError(false)

    setActiveKeyword(keyword)
    storeValue(item_id, "active_keyword", keyword)

    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_VIEWED, {
      keyword,
      country: selectedCountry,
      language: selectedLanguage,
    })

    const keywordTrackingKey = `${keyword}_${selectedCountry}_${selectedLanguage}`
    const trackingInfo = keywordTrackingCallTracker.current[keywordTrackingKey]
    if (trackingInfo && (Date.now() - trackingInfo.timestamp) < 2000) return

    keywordTrackingCallTracker.current[keywordTrackingKey] = {
      inProgress: true,
      timestamp: Date.now()
    }

    try {
      const response = await addKeywordTracking(
        keyword,
        selectedCountry,
        selectedLanguage,
        fromDate,
        toDate
      )

      if (isUpgradeResponse(response)) {
        setShowUpgradeModal(true)
        return
      }

      // Notify other pages (landing page) that a keyword was tracked for this item
      try {
        window.dispatchEvent(
          new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
        )
      } catch (err) {
        // ignore dispatch errors
      }
    } catch (error) {
      console.error("Error tracking keyword:", error)
      // Don't dispatch event on failure - only show error to user
      // Remove the fallback/local add dispatch to avoid confusion
      return;
    } finally {
      keywordTrackingCallTracker.current[keywordTrackingKey] = {
        inProgress: false,
        timestamp: Date.now()
      }
    }
  }

  const handleAddKeyword = async () => {
    if (!searchInput) return;
    if (!selectedCountry || !selectedLanguage) {
      triggerToast("Please select a country and language before adding keywords.", "error");
      return;
    }

    // Check if limit would be exceeded
    if (wouldExceedLimit()) {
      const keywordsToRemove = getKeywordsToRemove()
      setLimitWarningMessage(
        `Adding these keywords would exceed your plan limit. Please remove ${keywordsToRemove} keyword${keywordsToRemove > 1 ? 's' : ''} from the input to stay within your limit of ${getKeywordLimit()} keywords.`
      )
      setShowLimitWarningModal(true)
      return
    }

    // Check if limit is already reached
    if (isLimitReached()) {
      setLimitWarningMessage(
        `You have reached your plan limit of ${getKeywordLimit()} keywords. To add more keywords, please upgrade your plan or remove some existing keywords.`
      )
      setShowLimitWarningModal(true)
      return
    }

    const keywords = searchInput
      .split(",")
      .map(k => k.trim())
      .filter(k => k && k.length > 0 && k !== "game_data" && k.trim().length > 0);

    if (keywords.length === 0) {
      setSearchInput("");
      return;
    }

    const uniqueKeywords = [...new Set(keywords)];

    setIsTrackingInProgress(true);

    // Optimistically show chips immediately as pending
    const pairKeyOptimistic = `${selectedCountry}_${selectedLanguage}`;
    const newKeywordsToAdd = uniqueKeywords.filter(k => {
      const existing = keywordsByCountryLanguage[pairKeyOptimistic] || [];
      return !existing.includes(k);
    });
    const pairLabel = `${selectedCountry} (${selectedLanguage})`;
    const isPairNew = !trackedCountryLanguagePairs.includes(pairLabel);
    if (isPairNew) {
      setPendingPairs(prev => new Set([...prev, pairLabel]));
      setTrackedCountryLanguagePairs(prev => {
        if (prev.includes(pairLabel)) return prev;
        return [...prev, pairLabel];
      });
      setActiveTrackedPair(pairLabel);
    }

    if (newKeywordsToAdd.length > 0) {
      setPendingKeywords(prev => new Set([...prev, ...newKeywordsToAdd]));
      setTrackedKeywords(prev => [...new Set([...prev, ...newKeywordsToAdd])]);
      setKeywordsByCountryLanguage(prev => {
        const existing = prev[pairKeyOptimistic] || [];
        return {
          ...prev,
          [pairKeyOptimistic]: [...existing, ...newKeywordsToAdd.filter(k => !existing.includes(k))]
        };
      });
    }

    const apiCalls = uniqueKeywords.map(async (keyword) => {
      const key = `${keyword}_${selectedCountry}_${selectedLanguage}`;
      const trackingInfo = keywordTrackingCallTracker.current[key];
      if (trackingInfo && (Date.now() - trackingInfo.timestamp) < 2000) return null;

      keywordTrackingCallTracker.current[key] = {
        inProgress: true,
        timestamp: Date.now()
      };

      try {
        const response = await addKeywordTracking(
          keyword,
          selectedCountry,
          selectedLanguage,
          fromDate,
          toDate,
          () => triggerToast(`Keyword "${keyword}" was added but data is taking longer than expected. Please refresh later.`, "warning")
        );

        if (isUpgradeResponse(response)) {
          setShowUpgradeModal(true);
          return null;
        }

        return keyword;
      } catch (err) {
        console.error("Error tracking keyword:", err);
        return null;
      } finally {
        keywordTrackingCallTracker.current[key] = {
          inProgress: false,
          timestamp: Date.now()
        };
      }
    });

    const results = await Promise.allSettled(apiCalls);
    const successfullyAdded = results
      .filter(r => r.status === "fulfilled" && r.value)
      .map(r => r.value);

    // Clear all pending keywords and pairs that were optimistically added
    setPendingKeywords(new Set());
    setPendingPairs(new Set());
    setIsTrackingInProgress(false);

    if (successfullyAdded.length === 0) {
      // Remove all optimistically added chips since everything failed
      if (newKeywordsToAdd.length > 0) {
        setTrackedKeywords(prev => prev.filter(k => !newKeywordsToAdd.includes(k)));
        setKeywordsByCountryLanguage(prev => {
          const existing = prev[pairKeyOptimistic] || [];
          return {
            ...prev,
            [pairKeyOptimistic]: existing.filter(k => !newKeywordsToAdd.includes(k))
          };
        });
      }
      // Roll back the pair tab if it was newly added
      if (isPairNew) {
        setTrackedCountryLanguagePairs(prev => prev.filter(p => p !== pairLabel));
      }
      setSearchInput("");
      return;
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORDS_ADDED, {
      keyword_count: successfullyAdded.length,
      country: selectedCountry,
      language: selectedLanguage,
    });

    // Remove chips for failed keywords, keep successful ones
    const failedKeywords = newKeywordsToAdd.filter(k => !successfullyAdded.includes(k));

    const pairKey = `${selectedCountry}_${selectedLanguage}`;
    setTrackedKeywords(prev => {
      const updated = prev.filter(k => !failedKeywords.includes(k));
      storeValue(item_id, "tracked_keywords", updated);
      return updated;
    });

    setKeywordsByCountryLanguage(prev => {
      const existing = prev[pairKey] || [];
      const updated = {
        ...prev,
        [pairKey]: existing.filter(k => !failedKeywords.includes(k))
      };
      storeValue(item_id, "keywords_by_country_language", updated);
      return updated;
    });

    const pair = `${selectedCountry} (${selectedLanguage})`;
    setTrackedCountryLanguagePairs(prev => {
      const updated = prev.includes(pair) ? prev : [...prev, pair];
      console.log("LOGGINGGGG HEREEEE 1742");

      storeValue(item_id, "tracked_country_language_pairs", updated);
      return updated;
    });

    setActiveTrackedPair(pair);
    setActiveKeyword(successfullyAdded[0]);
    setTrackedCountries(prev => {
      if (prev.includes(selectedCountry)) return prev;
      const updated = [...prev, selectedCountry];
      storeValue(item_id, "tracked_countries", updated);
      return updated;
    });

    apiCallTracker.current[`${selectedCountry}_${selectedLanguage}`] = {
      inProgress: false,
      timestamp: Date.now()
    };

    successfullyAdded.forEach(keyword => {
      window.dispatchEvent(
        new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
      );
    });

    setInitialFetchDone(true);
    setSearchInput("");
  };

  const handleAddSuggestedKeywords = async (keywords) => {
    if (!keywords || keywords.length === 0 || !selectedCountry || !selectedLanguage) {
      return;
    }

    const uniqueKeywords = [...new Set(keywords)];

    // Optimistically show chips immediately as pending
    const pairKeyOptimistic2 = `${selectedCountry}_${selectedLanguage}`;
    const newKeywordsToAdd2 = uniqueKeywords.filter(k => {
      const existing = keywordsByCountryLanguage[pairKeyOptimistic2] || [];
      return !existing.includes(k);
    });
    const pairLabel2 = `${selectedCountry} (${selectedLanguage})`;
    const isPairNew2 = !trackedCountryLanguagePairs.includes(pairLabel2);
    if (isPairNew2) {
      setPendingPairs(prev => new Set([...prev, pairLabel2]));
      setTrackedCountryLanguagePairs(prev => {
        if (prev.includes(pairLabel2)) return prev;
        return [...prev, pairLabel2];
      });
      setActiveTrackedPair(pairLabel2);
    }
    setIsTrackingInProgress(true);

    if (newKeywordsToAdd2.length > 0) {
      setPendingKeywords(prev => new Set([...prev, ...newKeywordsToAdd2]));
      setTrackedKeywords(prev => [...new Set([...prev, ...newKeywordsToAdd2])]);
      setKeywordsByCountryLanguage(prev => {
        const existing = prev[pairKeyOptimistic2] || [];
        return {
          ...prev,
          [pairKeyOptimistic2]: [...existing, ...newKeywordsToAdd2.filter(k => !existing.includes(k))]
        };
      });
    }

    const apiCalls = uniqueKeywords.map(async (keyword) => {
      const key = `${keyword}_${selectedCountry}_${selectedLanguage}`;
      const trackingInfo = keywordTrackingCallTracker.current[key];
      if (trackingInfo && (Date.now() - trackingInfo.timestamp) < 2000) return null;

      keywordTrackingCallTracker.current[key] = {
        inProgress: true,
        timestamp: Date.now()
      };

      try {
        const response = await addKeywordTracking(
          keyword,
          selectedCountry,
          selectedLanguage,
          fromDate,
          toDate,
          () => triggerToast(`Keyword "${keyword}" was added but data is taking longer than expected. Please refresh later.`, "warning")
        );

        if (isUpgradeResponse(response)) {
          setShowUpgradeModal(true);
          return null;
        }

        return keyword;
      } catch (err) {
        console.error("Error tracking keyword:", err);
        return null;
      } finally {
        keywordTrackingCallTracker.current[key] = {
          inProgress: false,
          timestamp: Date.now()
        };
      }
    });

    const results = await Promise.allSettled(apiCalls);
    const successfullyAdded = results
      .filter(r => r.status === "fulfilled" && r.value)
      .map(r => r.value);

    setPendingKeywords(new Set());
    setPendingPairs(new Set());
    setIsTrackingInProgress(false);

    if (successfullyAdded.length === 0) {
      if (newKeywordsToAdd2.length > 0) {
        setTrackedKeywords(prev => prev.filter(k => !newKeywordsToAdd2.includes(k)));
        setKeywordsByCountryLanguage(prev => {
          const existing = prev[pairKeyOptimistic2] || [];
          return { ...prev, [pairKeyOptimistic2]: existing.filter(k => !newKeywordsToAdd2.includes(k)) };
        });
      }
      if (isPairNew2) {
        setTrackedCountryLanguagePairs(prev => prev.filter(p => p !== pairLabel2));
      }
      return;
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORDS_ADDED, {
      keyword_count: successfullyAdded.length,
      country: selectedCountry,
      language: selectedLanguage,
      source: "suggested",
    });

    const failedKeywords2 = newKeywordsToAdd2.filter(k => !successfullyAdded.includes(k));

    const pairKey = `${selectedCountry}_${selectedLanguage}`;
    setTrackedKeywords(prev => {
      const updated = prev.filter(k => !failedKeywords2.includes(k));
      storeValue(item_id, "tracked_keywords", updated);
      return updated;
    });

    setKeywordsByCountryLanguage(prev => {
      const existing = prev[pairKey] || [];
      const updated = {
        ...prev,
        [pairKey]: existing.filter(k => !failedKeywords2.includes(k))
      };
      storeValue(item_id, "keywords_by_country_language", updated);
      return updated;
    });

    const pair = `${selectedCountry} (${selectedLanguage})`;
    setTrackedCountryLanguagePairs(prev => {
      const updated = prev.includes(pair) ? prev : [...prev, pair];
      console.log("LOGGINGGGG HEREEEE 1889");

      storeValue(item_id, "tracked_country_language_pairs", updated);
      return updated;
    });

    setActiveTrackedPair(pair);
    setActiveKeyword(successfullyAdded[0]);
    setTrackedCountries(prev => {
      if (prev.includes(selectedCountry)) return prev;
      const updated = [...prev, selectedCountry];
      storeValue(item_id, "tracked_countries", updated);
      return updated;
    });

    apiCallTracker.current[`${selectedCountry}_${selectedLanguage}`] = {
      inProgress: false,
      timestamp: Date.now()
    };

    successfullyAdded.forEach(keyword => {
      window.dispatchEvent(
        new CustomEvent("keywordTracked", { detail: { item_id, keyword } })
      );
    });

    setInitialFetchDone(true);
  };


  const removeAppFromTracking = async () => {
    const success = await removeEntity(null, "keyword")
    if (success) {
      trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_REMOVED, {
        country: selectedCountry,
        language: selectedLanguage,
        scope: "app",
      })
      try {
        const prefix = `keyword_tracking_${item_id}_`
        Object.keys(localStorage).forEach(key => {
          if (key.startsWith(prefix)) localStorage.removeItem(key)
        })
      } catch (e) { }
      apiCallTracker.current = {}
      navigate("/keyword-tracking")
    }
  }

  const handleRemoveEntity = async (keywordId) => {
    if (!keywordId) {
      // Removing entire app
      const success = await removeEntity(null, "keyword");
      if (success) {
        trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_REMOVED, {
          country: selectedCountry,
          language: selectedLanguage,
          scope: "app",
        });

        // Clear local storage for this app to prevent zombie data resurrection
        try {
          const prefix = `keyword_tracking_${item_id}_`
          Object.keys(localStorage).forEach(key => {
            if (key.startsWith(prefix)) {
              localStorage.removeItem(key)
            }
          });
        } catch (e) {
          console.error("Error clearing cleanup storage:", e)
        }

        // Clear API call tracker
        apiCallTracker.current = {};

        navigate("/keyword-tracking");
      } else {
        console.error("Failed to remove app from backend");
      }
      return;
    }

    // Call backend to remove the keyword — scoped to the current country/language pair
    const success = await removeEntity(keywordId, "keyword", {
      country: selectedCountry,
      language: selectedLanguage,
    });

    if (!success) {
      console.error("Failed to remove keyword from backend");
      return;
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_REMOVED, {
      keyword: keywordId,
      country: selectedCountry,
      language: selectedLanguage,
    });

    // 1️⃣ Remove from trackedKeywords — only if no other pair still references this keyword
    const currentPairKey = selectedCountry && selectedLanguage ? `${selectedCountry}_${selectedLanguage}` : null
    const otherPairs = { ...keywordsByCountryLanguage }
    if (currentPairKey) delete otherPairs[currentPairKey]
    const stillNeededElsewhere = Object.values(otherPairs).flat().includes(keywordId)

    if (!stillNeededElsewhere) {
      const updatedTrackedKeywords = trackedKeywords.filter(k => k !== keywordId)
      setTrackedKeywords(updatedTrackedKeywords)
      storeValue(item_id, "tracked_keywords", updatedTrackedKeywords)
    }

    // 2️⃣ Remove from current country-language pair and check if it becomes empty
    let pairBecameEmpty = false;
    let nextBest = null;

    if (selectedCountry && selectedLanguage) {
      const pairKey = `${selectedCountry}_${selectedLanguage}`;

      // Calculate what the updated state will look like
      const updatedKeywords = (keywordsByCountryLanguage[pairKey] || []).filter(k => k !== keywordId);

      // Check if this pair will become empty
      if (updatedKeywords.length === 0) {
        pairBecameEmpty = true;

        // Calculate next best pair BEFORE updating state
        // Create a copy of keywordsByCountryLanguage without the current pair
        const remainingPairs = { ...keywordsByCountryLanguage };
        delete remainingPairs[pairKey];

        // Find the pair with most keywords from remaining pairs
        let maxCount = 0;
        let bestPairKey = null;

        Object.entries(remainingPairs).forEach(([key, keywords]) => {
          const count = keywords.length;
          if (count > maxCount) {
            maxCount = count;
            bestPairKey = key;
          }
        });

        if (bestPairKey) {
          const parts = bestPairKey.split('_')
          const country = parts[0]
          const language = parts.slice(1).join('_')
          nextBest = { pair: `${country} (${language})`, country, language };
        }
      }

      // Now update the state
      setKeywordsByCountryLanguage(prev => {
        const updated = {
          ...prev,
          [pairKey]: updatedKeywords
        };

        if (pairBecameEmpty) {
          delete updated[pairKey];
        }

        storeValue(item_id, "keywords_by_country_language", updated);
        return updated;
      });

      // If pair became empty, remove it from trackedCountryLanguagePairs
      if (pairBecameEmpty) {
        const pair = `${selectedCountry} (${selectedLanguage})`;
        setTrackedCountryLanguagePairs(prev => {
          const updated = prev.filter(p => p !== pair);
          console.log("LOGGINGGGG HEREEEE 2042");

          storeValue(item_id, "tracked_country_language_pairs", updated);
          return updated;
        });

        // Auto-select the next best pair (calculated earlier)
        if (nextBest) {
          setActiveTrackedPair(nextBest.pair);
          setSelectedCountry(nextBest.country);
          setSelectedLanguage(nextBest.language);
          storeValue(item_id, "active_tracked_pair", nextBest.pair);
          storeValue(item_id, "selected_country", nextBest.country);
          storeValue(item_id, "selected_language", nextBest.language);
        } else {
          // No pairs left — app has zero tracked keywords, remove it from tracking
          await removeAppFromTracking()
          return
        }
      }
    }

    // 3️⃣ Clear activeKeyword if it was removed
    if (activeKeyword === keywordId) {
      setActiveKeyword(null);
    }

    // 4️⃣ Reset API call tracker for current pair
    if (selectedCountry && selectedLanguage && !pairBecameEmpty) {
      const callKey = `${selectedCountry}_${selectedLanguage}`;
      apiCallTracker.current[callKey] = {
        inProgress: false,
        timestamp: Date.now()
      };
    }

    // 5️⃣ Do not refresh full table; hook already optimistically updates keywordTrackingData
  };


  const [keywordsCopied, setKeywordsCopied] = useState(false)

  const handleCopyKeywords = () => {
    const keywords = getKeywordsForCurrentPair()
    if (keywords.length === 0) return
    navigator.clipboard.writeText(keywords.join(', ')).then(() => {
      setKeywordsCopied(true)
      setTimeout(() => setKeywordsCopied(false), 2000)
    }).catch(() => {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement('textarea')
      textArea.value = keywords.join(', ')
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setKeywordsCopied(true)
      setTimeout(() => setKeywordsCopied(false), 2000)
    })
  }

  const toggleShowAllKeywords = () => setShowAllKeywords(prev => !prev)

  const handleOpen = () => setShowModal(true)
  const handleClose = () => setShowModal(false)

  // Reset keyword tracking call tracker when country or language changes
  useEffect(() => {
    if (selectedCountry && selectedLanguage) {
      // Clear all tracking for this country/language pair
      Object.keys(keywordTrackingCallTracker.current).forEach(key => {
        if (key.includes(`_${selectedCountry}_${selectedLanguage}`)) {
          keywordTrackingCallTracker.current[key] = {
            inProgress: false,
            timestamp: Date.now()
          }
        }
      })
    }
  }, [selectedCountry, selectedLanguage])
  const memoizedResults = useMemo(() => {
    return keywordTrackingData && typeof keywordTrackingData === 'object' ? Object.values(keywordTrackingData) : []
  }, [keywordTrackingData])

  // Stable search handlers
  const handleSearchChange = useCallback(e => {
    setSearchInput(e.target.value)
  }, [])

  const handleSearchClick = useCallback(() => {
    fetchKeywordTracking(selectedCountry, selectedLanguage, fromDate, toDate)
  }, [
    fetchKeywordTracking,
    selectedCountry,
    selectedLanguage,
    fromDate,
    toDate,
  ])

  const handleSelectApp = useCallback(app => {
    console.log("Selected app:", app)
  }, [])

  const handleRemoveTrackedPair = async (pair, opts = {}) => {
    if (!pair) return

    // Parse country and language from "Country (Language)" if possible
    const match = pair.match(/(.*) \((.*)\)/)
    const country = match ? match[1] : pair
    const language = match ? match[2] : null

    // Determine if this pair has keywords
    const pairKey = language ? `${country}_${language}` : null
    const hasKeywords = pairKey
      ? (keywordsByCountryLanguage[pairKey] || []).length > 0
      : false

    // If the pair has keywords but caller didn't explicitly request deletion of keywords, abort.
    if (hasKeywords && !opts.deleteKeywords) {
      // Do nothing — require explicit confirmation to delete keywords along with the pair
      return
    }

    // 0️⃣ Delete from backend if requested - wait for completion
    if (opts.deleteKeywords && hasKeywords && pairKey) {
      const keywordsToRemove = keywordsByCountryLanguage[pairKey] || []
      try {
        // Wait for all keyword removals to complete
        const removalResults = await Promise.all(
          keywordsToRemove.map(kw => removeEntity(kw, "keyword", { country, language }))
        )

        // Check if any removals failed
        const failedRemovals = removalResults.filter(result => !result)
        if (failedRemovals.length > 0) {
          console.error(`Failed to remove ${failedRemovals.length} keywords from backend`)
          // Don't proceed with UI removal if backend removal failed
          return
        }
      } catch (error) {
        console.error("Error removing keywords for pair:", error)
        // Don't proceed with UI removal if backend removal failed
        return
      }
    }

    trackEvent(ANALYTICS_EVENTS.FEATURE.KEYWORD_TRACKING_KEYWORD_REMOVED, {
      country,
      language,
      scope: "pair",
    })

    // 1) Remove the pair from trackedCountryLanguagePairs and persist
    setTrackedCountryLanguagePairs(prev => {
      const updated = prev.filter(item => item !== pair)
      console.log("LOGGINGGGG HEREEEE 2193");

      storeValue(item_id, "tracked_country_language_pairs", updated)
      return updated
    })

    // 2) Remove keywords associated with this pair (if any) and persist
    const keywordsToRemove = pairKey ? (keywordsByCountryLanguage[pairKey] || []) : []
    setKeywordsByCountryLanguage(prev => {
      const updated = { ...prev }
      if (language) {
        const pKey = `${country}_${language}`
        if (pKey in updated) {
          delete updated[pKey]
        }
      } else {
        // if no language, remove any keys for this country
        Object.keys(updated).forEach(k => {
          if (k.startsWith(`${country}_`)) delete updated[k]
        })
      }
      storeValue(item_id, "keywords_by_country_language", updated)
      return updated
    })

    // 2.5) Remove keywords from global trackedKeywords — but only if not referenced by other pairs.
    // The same keyword name (e.g. "action") can exist in multiple country-language pairs.
    // Removing it blindly from the global list would make it disappear from those other pairs.
    if (keywordsToRemove.length > 0) {
      // Compute which keywords are still referenced by pairs OTHER than the one being removed.
      // Use closure value of keywordsByCountryLanguage (pre-update); subtract the pair being deleted.
      const remaining = { ...keywordsByCountryLanguage }
      if (pairKey) delete remaining[pairKey]
      const stillNeeded = new Set(Object.values(remaining).flat())

      setTrackedKeywords(prev => {
        const updated = prev.filter(kw => !keywordsToRemove.includes(kw) || stillNeeded.has(kw))
        storeValue(item_id, "tracked_keywords", updated)
        return updated
      })
    }

    // 3) Only remove country from trackedCountries if no other pairs for it remain
    const otherPairsForCountry = trackedCountryLanguagePairs.filter(p => {
      if (p === pair) return false
      const m = p.match(/(.*) \((.*)\)/)
      return m && m[1] === country
    })
    if (otherPairsForCountry.length === 0) {
      setTrackedCountries(prev => {
        const updated = prev.filter(c => c !== country)
        storeValue(item_id, "tracked_countries", updated)
        return updated
      })
    }

    // 4) Clear active tracked pair/country if it was the one removed
    if (activeTrackedPair === pair) {
      // Find the next best pair to select based on INDEX (keep user context)
      // We use the PREVIOUS trackedCountryLanguagePairs to find the index
      const currentIndex = trackedCountryLanguagePairs.indexOf(pair)

      // The new list will have the item removed.
      // If we were at index i, we try to stay at index i (which is the next item), 
      // or go to i-1 if we were at the end.

      const newPairs = trackedCountryLanguagePairs.filter(p => p !== pair)
      let nextPair = null

      if (newPairs.length > 0) {
        // If current index is valid in new array (meaning there was an item AFTER), use it.
        // Otherwise use the last item (length - 1).
        const nextIndex = currentIndex < newPairs.length ? currentIndex : newPairs.length - 1
        nextPair = newPairs[nextIndex]
      }

      if (nextPair) {
        setActiveTrackedPair(nextPair);
        storeValue(item_id, "active_tracked_pair", nextPair);

        const match = nextPair.match(/(.*) \((.*)\)/)
        if (match) {
          const nextCountry = match[1]
          const nextLanguage = match[2]
          setSelectedCountry(nextCountry);
          setSelectedLanguage(nextLanguage);
          storeValue(item_id, "selected_country", nextCountry);
          storeValue(item_id, "selected_language", nextLanguage);

          // Also set active tracked country
          setActiveTrackedCountry(nextCountry)
          storeValue(item_id, "active_tracked_country", nextCountry)
        }

        // Force data fetch for the new selection
        // IMPORTANT: Reset trackers so the fetch actually happens!
        lastSelectedPairRef.current = null
        if (match) {
          const callKey = `${match[1]}_${match[2]}`
          if (apiCallTracker.current) {
            apiCallTracker.current[callKey] = {
              inProgress: false,
              timestamp: Date.now()
            }
          }
        }

        setInitialFetchDone(true)

      } else {
        // No pairs left — app has zero tracked keywords, remove it from tracking
        await removeAppFromTracking()
        return
      }
    }
    if (activeTrackedCountry === country && trackedCountries.filter(c => c !== country).length === 0) {
      setActiveTrackedCountry("")
      storeValue(item_id, "active_tracked_country", "")
    }
  }

  // ✅ Handle File Import (CSV or TXT)
  const handleImportKeywords = event => {
    const file = event.target.files[0]
    if (!file) return

    const reader = new FileReader()
    reader.onload = e => {
      const content = e.target.result

      // Split by commas or new lines
      const keywords = content
        .split(/[\n,]+/)
        .map(k => k.trim())
        .filter(Boolean)

      if (keywords.length === 0) {
        alert("No keywords found in the file.")
        return
      }

      // Load into the keyword input for review
      setSearchInput(keywords.join(", "))

      // Optional: automatically add them
      // onAddKeyword()

      // Reset file input so user can re-upload the same file if needed
      event.target.value = ""
    }

    reader.readAsText(file)
  }

  // ✅ Handle Sample File Download
  const handleDownloadSample = () => {
    const sampleFilePath = "/samples/sample_keywords.txt" // stored in /public/samples/

    const link = document.createElement("a")
    link.href = sampleFilePath
    link.download = "sample_keywords.txt"
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  // const readableDate = new Date(trackedApp.update_time * 1000).toLocaleString()


  // Early return for 404 Not Found (after all hooks)
  if (notFoundError && !keywordsLoading) {
    return (
      <>
        <SEO title="Page Not Found" description="The requested app could not be found." />
        <NotFound
          message="The app you're looking for doesn't exist or has been removed."
          linkText="Back to Dashboard"
          linkTo="/"
        />
      </>
    )
  }

  return (
    <React.Fragment>
      <SEO title={trackedApp?.name || "Keyword Details"} description="Detailed keyword tracking analysis." />
      <div className="keyword_track_detail">
        <div className="container-fluid p-4">
          <div className="row">
            <div className="col-sm-12">
              <div className="keyword_track_detail_hed">
                <h3 className="text-white">Keyword Tracking</h3>
              </div>

              <AppHeader
                trackedApp={trackedApp}
                onRemoveApp={() => handleRemoveEntity(null)}
              />

              {/* Search Instructions */}
              <div style={{ marginTop: '30px', marginBottom: '20px', textAlign: 'left' }}>
                <p style={{
                  color: '#9ca3af',
                  fontSize: '14px',
                  margin: '0',
                  fontStyle: 'italic',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <i
                    className="fas fa-info-circle"
                    style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontStyle: 'normal'
                    }}
                  ></i>
                  Start searching for keywords to track their performance - you can adjust country and language settings anytime
                </p>
              </div>

              {/* Search Bar with Country/Language Dropdowns and Import Button */}
              <div className="row align-items-center mb-3">
                <div className="col-lg-7 col-md-6 col-sm-12">
                  <KeywordSearch
                    searchInput={searchInput}
                    setSearchInput={setSearchInput}
                    onAddKeyword={handleAddKeyword}
                    addingKeyword={addingKeyword || isTrackingInProgress}
                    selectedCountry={selectedCountry}
                    selectedLanguage={selectedLanguage}
                    onGenerateKeywords={() => setShowSuggestedKeywordsModal(true)}
                    keywordCount={getTotalKeywordCount()}
                    keywordLimit={getKeywordLimit()}
                    planLoading={planLoading}
                    onLimitReached={() => {
                      console.log('onLimitReached called, setting showUpgradeModal to true')
                      setShowUpgradeModal(true)
                    }}
                  />
                </div>
                <div className="col-lg-5 col-md-6 col-sm-12">
                  <div className="d-flex gap-2 align-items-center justify-content-end justify-content-sm-start mt-sm-2 mt-md-0 flex-wrap" style={{ paddingTop: windowWidth < 768 ? '16px' : '0' }}>
                    {/* Import Keywords Button - Desktop Only */}
                    {windowWidth >= 768 && (
                      <button
                        onClick={() => {
                          console.log('Import button clicked, isLimitReached():', isLimitReached())
                          if (isLimitReached()) {
                            console.log('Limit reached, showing upgrade modal')
                            setShowUpgradeModal(true)
                          } else {
                            document.getElementById('keywordFileUpload').click()
                          }
                        }}
                        style={{
                          backgroundColor: !selectedCountry || !selectedLanguage || isLimitReached() ? '#4b5563' : COLORS.primary,
                          color: COLORS.white,
                          padding: "10px",
                          borderRadius: "4px",
                          border: "none",
                          cursor: !selectedCountry || !selectedLanguage || isLimitReached() ? 'not-allowed' : 'pointer',
                          fontSize: "14px",
                          width: "40px",
                          height: "40px",
                          display: "inline-flex",
                          alignItems: "center",
                          justifyContent: "center",
                          opacity: !selectedCountry || !selectedLanguage || isLimitReached() ? 0.6 : 1,
                          transition: "all 0.2s ease",
                          flexShrink: 0
                        }}
                        title={isLimitReached() ? "Keyword limit reached. Click to upgrade your plan." : "Import Keywords"}
                        disabled={!selectedCountry || !selectedLanguage || isLimitReached()}
                      >
                        <i
                          className="fas fa-upload"
                          style={{ fontSize: "12px" }}
                        ></i>
                      </button>
                    )}

                    {/* Country — shared with the language picker below and with
                        CategoryTracking's identical block via FlagSelectDropdown,
                        so the two controls can't drift out of visual sync again. */}
                    <FlagSelectDropdown
                      options={countriesData.map(country => country.name)}
                      value={selectedCountry}
                      onChange={handleCountryChange}
                      placeholder="Select Country"
                      disabled={isTrackingInProgress}
                      {...KEYWORD_TRACKING_THEME}
                      minWidth={140}
                      maxWidth={200}
                      icon={(countryName) => {
                        const FlagComponent = getCountryFlag(countryName)
                        return FlagComponent ? <FlagComponent style={{ width: '16px', height: 'auto' }} /> : null
                      }}
                    />

                    {/* Language */}
                    <FlagSelectDropdown
                      options={availableLanguages}
                      value={selectedLanguage}
                      onChange={handleLanguageChange}
                      placeholder="Select Language"
                      disabled={!selectedCountry || availableLanguages.length === 0 || isTrackingInProgress}
                      {...KEYWORD_TRACKING_THEME}
                      minWidth={120}
                      maxWidth={180}
                      noOptionsText="Select a country first"
                    />
                  </div>
                </div>
              </div>

              {/* Hidden file input for import functionality */}
              <input
                id="keywordFileUpload"
                type="file"
                accept=".csv,.txt"
                style={{ display: "none" }}
                onChange={handleImportKeywords}
                disabled={!selectedCountry || !selectedLanguage}
              />

              {/* Tracked Data Availability Section - Full Width */}
              {trackedCountryLanguagePairs.length > 0 && (
                <div style={{
                  background: '#1a1b23',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px',
                  border: '1px solid #3A3B47'
                }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <label style={{
                      color: COLORS.white,
                      fontSize: "16px",
                      fontWeight: 600
                    }}>
                      Tracked Data Availability
                    </label>
                  </div>

                  <div style={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '8px',
                    marginBottom: '12px'
                  }}>
                    {trackedCountryLanguagePairs.map((pair, index) => {
                      let isActive = false;

                      if (activeTrackedPair) {
                        isActive = pair === activeTrackedPair;
                      } else if (activeTrackedCountry) {
                        isActive = pair.startsWith(activeTrackedCountry);
                      } else if (selectedCountry && selectedLanguage) {
                        const currentSelection = `${selectedCountry} (${selectedLanguage})`;
                        isActive = pair === currentSelection || pair === selectedCountry;
                      }

                      const match = pair.match(/(.*) \((.*)\)/)
                      const pairKey = match ? `${match[1]}_${match[2]}` : ''
                      const allKeywords = (keywordsByCountryLanguage[pairKey] || []).filter(k => k !== "game_data")
                      const isPairPending = pendingPairs.has(pair)

                      return (
                        <div
                          key={`${pair}-${index}`}
                          style={{
                            background: isActive ? COLORS.primary : '#2A2B37',
                            color: isPairPending ? '#6b7280' : COLORS.white,
                            padding: '6px 10px',
                            borderRadius: '6px',
                            fontSize: '0.9rem',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            border: '1px solid #3A3B47',
                            animation: isPairPending ? 'pulse 1.5s ease-in-out infinite' : 'none',
                          }}
                        >
                          <div
                            style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                            onClick={() => handleTrackedPairClick(pair)}
                          >
                            {/* Country Flag */}
                            {(() => {
                              const actualCountry = pair.includes("(") ? pair.split("(")[0].trim() : pair;
                              const FlagComponent = (() => {
                                try {
                                  return getCountryFlag(actualCountry);
                                } catch (e) {
                                  return null;
                                }
                              })();

                              return FlagComponent ? (
                                <FlagComponent
                                  style={{
                                    width: '16px',
                                    height: 'auto',
                                    marginRight: '6px',
                                    verticalAlign: 'middle',
                                  }}
                                />
                              ) : null;
                            })()}
                            <span>{pair}</span>
                          </div>

                          {/* Keyword Count Badge / Pending indicator */}
                          {isPairPending ? (
                            <span style={{
                              color: '#6b7280',
                              fontSize: '0.7rem',
                              fontStyle: 'italic',
                              marginLeft: '4px'
                            }}>
                              adding…
                            </span>
                          ) : !keywordTrackingDataLoaded && allKeywords.length === 0 ? (
                            // Genuinely zero keywords and "not fetched yet" both look like an
                            // empty array here — on a fresh browser session (no localStorage
                            // cache) that used to render nothing at all, which read as the
                            // count being missing. Show a loading badge instead until the
                            // mount-time fetch resolves, then it'll settle on the real count.
                            <span style={{
                              color: '#6b7280',
                              fontSize: '0.7rem',
                              fontStyle: 'italic',
                              marginLeft: '4px'
                            }}>
                              loading…
                            </span>
                          ) : (
                            <span style={{
                              background: isActive ? '#2A2B37' : COLORS.primary,
                              color: COLORS.white,
                              padding: '2px 6px',
                              borderRadius: '10px',
                              fontSize: '0.7rem',
                              fontWeight: 600,
                              marginLeft: '4px'
                            }}>
                              {allKeywords.length}
                            </span>
                          )}

                          {/* Remove button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              // Show confirmation modal instead of direct removal
                              setPairToRemove(pair);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: COLORS.white,
                              cursor: 'pointer',
                              padding: '2px',
                              borderRadius: '2px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              opacity: 0.7,
                              '&:hover': {
                                opacity: 1,
                                backgroundColor: 'rgba(255, 255, 255, 0.1)'
                              }
                            }}
                            onMouseEnter={(e) => {
                              e.target.style.opacity = '1';
                              e.target.style.backgroundColor = 'rgba(255, 255, 255, 0.1)';
                            }}
                            onMouseLeave={(e) => {
                              e.target.style.opacity = '0.7';
                              e.target.style.backgroundColor = 'transparent';
                            }}
                            title="Remove country"
                          >
                            <i className="fas fa-times" style={{ fontSize: '12px' }}></i>
                          </button>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}

              {/* Tracked Keywords Section - Full Width */}
              {getKeywordsForActivePair().length > 0 && (
                <div style={{
                  background: '#1a1b23',
                  borderRadius: '8px',
                  padding: '20px',
                  marginBottom: '20px',
                  border: '1px solid #3A3B47'
                }}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <label
                      style={{
                        fontWeight: 600,
                        color: COLORS.white,
                        fontSize: "16px",
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                      }}
                    >
                      Tracked Keywords ({getKeywordsForActivePair().length})
                      {pendingKeywords.size > 0 && (
                        <span style={{
                          fontSize: '12px',
                          color: '#9ca3af',
                          fontWeight: 400,
                        }}>
                          adding…
                        </span>
                      )}
                    </label>
                    <button
                      onClick={handleCopyKeywords}
                      title="Copy all keywords"
                      style={{
                        background: keywordsCopied ? '#1a3a2a' : '#2A2B37',
                        border: `1px solid ${keywordsCopied ? '#22c55e' : '#3A3B47'}`,
                        color: keywordsCopied ? '#22c55e' : '#9ca3af',
                        borderRadius: '6px',
                        padding: '5px 10px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '5px',
                        transition: 'all 0.2s ease',
                        whiteSpace: 'nowrap',
                        flexShrink: 0,
                      }}
                    >
                      <i className={keywordsCopied ? 'fas fa-check' : 'fas fa-copy'} style={{ fontSize: '12px' }}></i>
                      <span style={{ display: 'inline' }}>{keywordsCopied ? 'Copied!' : 'Copy'}</span>
                    </button>
                  </div>

                  <div>
                    <div style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: '8px',
                      marginBottom: '12px'
                    }}>
                      {(showAllKeywords
                        ? getKeywordsForActivePair()
                        : getKeywordsForActivePair().slice(0, 6)
                      ).map((keyword, index) => {
                        const fullIndex = getKeywordsForActivePair().indexOf(keyword)
                        const isPending = pendingKeywords.has(keyword)
                        return (
                          <span
                            key={keyword}
                            className="keyword-tag"
                            draggable={!isPending}
                            onDragStart={e => {
                              e.dataTransfer.setData("text/plain", fullIndex.toString())
                            }}
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                              const draggedIndex = Number(e.dataTransfer.getData("text/plain"))
                              const droppedIndex = fullIndex
                              if (draggedIndex === droppedIndex) return

                              const pairKey = getActivePairKey()
                              setKeywordsByCountryLanguage(prev => {
                                const keywords = [...(prev[pairKey] || [])]
                                const [movedKeyword] = keywords.splice(draggedIndex, 1)
                                keywords.splice(droppedIndex, 0, movedKeyword)

                                // Save updated keywords immediately
                                storeValue(
                                  item_id,
                                  "keywords_by_country_language",
                                  {
                                    ...prev,
                                    [pairKey]: keywords,
                                  }
                                )

                                return {
                                  ...prev,
                                  [pairKey]: keywords,
                                }
                              })
                            }}
                            style={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              background: '#2A2B37',
                              color: isPending ? '#6b7280' : COLORS.white,
                              padding: '6px 12px',
                              borderRadius: '16px',
                              fontSize: '0.85rem',
                              border: '1px solid #3A3B47',
                              transition: 'all 0.2s ease',
                              animation: isPending ? 'pulse 1.5s ease-in-out infinite' : 'none',
                            }}
                          >
                            {keyword}
                            {!isPending && (
                              <button
                                onClick={e => {
                                  e.preventDefault()
                                  setKeywordToDelete(keyword)
                                }}
                                style={{
                                  marginLeft: "6px",
                                  border: "none",
                                  background: "transparent",
                                  cursor: "pointer",
                                  fontSize: "0.8rem",
                                  color: "#888",
                                }}
                                aria-label={`Remove keyword ${keyword}`}
                              >
                                ×
                              </button>
                            )}
                          </span>
                        )
                      })}
                    </div>

                    {getKeywordsForActivePair().length > 6 && (
                      <button
                        onClick={() => setShowAllKeywords(prev => !prev)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: COLORS.primary,
                          fontWeight: 600,
                          cursor: 'pointer',
                          fontSize: '0.85rem',
                          padding: '4px 0'
                        }}
                      >
                        {showAllKeywords
                          ? "Show Less"
                          : `Show More (${getKeywordsForActivePair().length - 6})`}
                      </button>
                    )}
                  </div>
                </div>
              )}

              {/* No Keywords Message */}
              {getKeywordsForActivePair().length === 0 && selectedCountry && selectedLanguage && (
                <div style={{
                  textAlign: 'center',
                  padding: '20px',
                  color: '#94a3b8',
                  fontSize: '14px',
                  fontStyle: 'italic',
                  background: '#1a1b23',
                  borderRadius: '8px',
                  border: '1px solid #3A3B47',
                  marginBottom: '20px'
                }}>
                  No tracked keywords for this country and language. Add keywords above to start tracking.
                </div>
              )}

              <Modal
                isOpen={!!keywordToDelete}
                toggle={() => setKeywordToDelete(null)}
                centered
              >
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Deletion</h5>
                  <button
                    type="button"
                    className="btn-close"
                    onClick={() => setKeywordToDelete(null)}
                    aria-label="Close"
                  />
                </div>
                <ModalBody style={{ textAlign: "center", padding: "20px" }}>
                  <p>
                    Are you sure you want to remove "<b>{keywordToDelete}</b>"
                    keyword?
                    <br /> Data for this keyword will be removed.
                  </p>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "10px",
                    }}
                  >
                    <Button
                      color="danger"
                      onClick={() => {
                        handleRemoveEntity(keywordToDelete) // actually remove
                        setKeywordToDelete(null) // close modal
                      }}
                    >
                      Yes
                    </Button>
                    <Button
                      color="secondary"
                      onClick={() => setKeywordToDelete(null)}
                    >
                      Cancel
                    </Button>
                  </div>
                </ModalBody>
              </Modal>

              {/* Pair Removal Confirmation Modal */}
              <Modal isOpen={!!pairToRemove} toggle={() => setPairToRemove(null)} centered>
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Removal</h5>
                  <button type="button" className="btn-close" onClick={() => setPairToRemove(null)} aria-label="Close" />
                </div>
                <ModalBody style={{ textAlign: 'center', padding: '20px' }}>
                  {(() => {
                    const pairKey = pairToRemove ? `${pairToRemove.includes('(') ? pairToRemove.split('(')[0].trim() : pairToRemove}_${pairToRemove.includes('(') ? pairToRemove.split('(')[1].replace(')', '').trim() : ''}` : '';
                    const hasKeywords = pairKey && keywordsByCountryLanguage[pairKey] && keywordsByCountryLanguage[pairKey].length > 0;

                    return hasKeywords ? (
                      <p>
                        The pair "<b>{pairToRemove}</b>" has tracked keywords. Removing it will also delete those keywords.
                        This action cannot be undone. Do you want to proceed?
                      </p>
                    ) : (
                      <p>Are you sure you want to remove "<b>{pairToRemove}</b>"? Data for this pair will be removed.</p>
                    );
                  })()}
                  <div style={{ display: 'flex', justifyContent: 'center', gap: '10px', marginTop: '12px' }}>
                    <Button color="danger" onClick={() => {
                      if (pairToRemove && typeof handleRemoveTrackedPair === 'function') {
                        handleRemoveTrackedPair(pairToRemove, { deleteKeywords: true })
                      }
                      setPairToRemove(null)
                    }}>Yes</Button>
                    <Button color="secondary" onClick={() => setPairToRemove(null)}>Cancel</Button>
                  </div>
                </ModalBody>
              </Modal>

              <div className="track_list">
                <DateRangeSelector
                  fromDate={fromDate}
                  toDate={toDate}
                  setFromDate={setFromDate}
                  setToDate={setToDate}
                />
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                    marginBottom: "6px",
                  }}
                >

                  {/* Export — Left */}
                  <div className="csv_btn">
                    <a
                      href="#"
                      onClick={e => {
                        e.preventDefault()
                        setShowExportModal(true)
                      }}
                    >
                      Export
                    </a>
                  </div>

                  {/* Last Updated — Right */}
                  <div
                    className="update_timestamp"
                    style={{
                      fontSize: "12px",
                      fontStyle: "italic",
                      color: "#94a3b8",
                      whiteSpace: "nowrap",
                      marginTop: "48px",
                    }}
                  >
                    {trackedApp?.update_time != null && !isNaN(trackedApp.update_time)
                      ? `Last updated: ${new Date(trackedApp.update_time * 1000).toLocaleString()}`
                      : "Last updated: N/A"}
                  </div>

                </div>

                <KeywordTrackingTable
                  keywordsLoading={keywordsLoading}
                  keywordTrackingData={keywordTrackingData}
                  item_id={item_id}
                  countryName={selectedCountry}
                  selectedLanguage={selectedLanguage}
                  planKeywordLimit={getAllowedKeywords()}
                  isOverLimit={isOverKeywordLimit()}
                  onUpgradeRequired={() => setShowUpgradeModal(true)}
                  onGenerateKeywords={() => setShowSuggestedKeywordsModal(true)}
                />
              </div>
              {/* Export Modal */}
              <ExportModal
                show={showExportModal}
                handleClose={() => setShowExportModal(false)}
                gameId={item_id} // 👈 required
                dataType="keyword" // 👈 required
              />
              <AppsTrackingPlanModal
                show={showUpgradeModal}
                handleClose={() => setShowUpgradeModal(false)}
                title=" Upgrade your plan to unlock this feature"
              />
              <UnsavedChangesModal
                show={showUnsavedChangesModal}
                onConfirm={confirmLeaveWithoutSaving}
                onCancel={cancelLeaveWithoutSaving}
                loading={isNavigating}
              />
              <SuggestedKeywordsModal
                isOpen={showSuggestedKeywordsModal}
                toggle={() => setShowSuggestedKeywordsModal(false)}
                itemId={item_id}
                selectedCountry={selectedCountry}
                selectedLanguage={selectedLanguage}
                onAddKeywords={handleAddSuggestedKeywords}
                trackedKeywords={getKeywordsForCurrentPair() || []}
                keywordCount={getTotalKeywordCount()}
                keywordCountLoading={!keywordTrackingDataLoaded}
                keywordLimit={getKeywordLimit()}
                onUpgrade={() => { setShowSuggestedKeywordsModal(false); setShowUpgradeModal(true); }}
              />

              {/* Limit Warning Modal */}
              {showLimitWarningModal && (
                <div className="modal-overlay" style={modalOverlayStyle}>
                  <div className="modal-container" style={modalContainerStyle}>
                    <div className="modal-header" style={modalHeaderStyle}>
                      <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                        Keyword Limit Reached
                      </h3>
                      <button
                        onClick={() => setShowLimitWarningModal(false)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#fff',
                          fontSize: '24px',
                          cursor: 'pointer',
                          padding: '0',
                          width: '30px',
                          height: '30px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    <div className="modal-body" style={{ padding: "24px" }}>
                      <p style={{ margin: 0, lineHeight: '1.5' }}>{limitWarningMessage}</p>
                    </div>
                    <div style={{ padding: "0 24px 24px", textAlign: 'right' }}>
                      <Button
                        color="primary"
                        onClick={() => setShowLimitWarningModal(false)}
                      >
                        I Understand
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </React.Fragment>
  )
}

export default KeywordDetails
